import React, { useState, useEffect, useRef, useCallback } from 'react';
import FadeIn from './FadeIn';

const heroImages = [
  {
    src: "https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_48_01%20PM.png",
    alt: "Padua Hills wedding ceremony outdoor",
    desktopPosition: "center center",
    mobilePosition: "center 30%",
  },
  {
    src: "https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_32_58%20PM.png",
    alt: "Padua Hills Theatre wedding reception",
    desktopPosition: "center center",
    mobilePosition: "center 25%",
  },
  {
    src: "https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_30_31%20PM.png",
    alt: "Padua Hills estate venue",
    desktopPosition: "center center",
    mobilePosition: "center 35%",
  },
  {
    src: "https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_39_46%20PM.png",
    alt: "Elegant wedding at Padua Hills",
    desktopPosition: "center center",
    mobilePosition: "center 20%",
  },
  {
    src: "https://ik.imagekit.io/aeu02xvjgn/34-padua-hills-theatre-claremont-persian-wedding-photography.jpg?updatedAt=1776888200067",
    alt: "Persian wedding at Padua Hills Theatre",
    desktopPosition: "center center",
    mobilePosition: "center 30%",
  },
  {
    src: "https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_34_30%20PM.png",
    alt: "Padua Hills wedding celebration",
    desktopPosition: "center center",
    mobilePosition: "center 45%",
  },
  {
    src: "https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_28_58%20PM.png",
    alt: "Padua Hills venue scenery",
    desktopPosition: "center center",
    mobilePosition: "center 30%",
  },
];

const INTERVAL = 5500;
const TRANSITION_DURATION = 1400;

const imageCache = new Map();
const preload = (src) => {
  if (imageCache.has(src)) return imageCache.get(src);
  const promise = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = () => resolve(src);
    img.src = src;
  });
  imageCache.set(src, promise);
  return promise;
};

const useHeroImages = () => {
  const [readySet, setReadySet] = useState(new Set());
  useEffect(() => {
    preload(heroImages[0].src).then(() => {
      setReadySet(prev => new Set([...prev, 0]));
    });
    heroImages.slice(1).forEach((img, i) => {
      setTimeout(() => {
        preload(img.src).then(() => {
          const idx = i + 1;
          setReadySet(prev => new Set([...prev, idx]));
        });
      }, (i + 1) * 800);
    });
  }, []);
  return readySet;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
};

const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const timerRef = useRef(null);
  const readySet = useHeroImages();
  const isMobile = useIsMobile();

  useEffect(() => {
    preload(heroImages[0].src).then(() => setFirstLoaded(true));
  }, []);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % heroImages.length);
    }, INTERVAL);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const goTo = useCallback((i) => {
    setCurrent(i);
    startTimer();
  }, [startTimer]);

  return (
    <section
      id="home"
      className="relative flex items-center justify-center text-center overflow-hidden"
      style={{ minHeight: '100svh', height: '100svh' }}
    >
      {/* Slides */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-brand-charcoal"
          style={{
            opacity: firstLoaded ? 0 : 1,
            transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`,
          }}
        />
        {heroImages.map((img, i) => {
          const position = isMobile ? img.mobilePosition : img.desktopPosition;
          return (
            <div
              key={img.src}
              className="absolute inset-0 overflow-hidden"
              style={{
                opacity: i === current && readySet.has(i) ? 1 : 0,
                transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`,
                willChange: 'opacity',
              }}
            >
              <img
                src={readySet.has(i) ? img.src : undefined}
                alt={img.alt}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: position,
                  transform: isMobile ? 'scale(1.02)' : 'scale(1)',
                  transformOrigin: 'center center',
                  transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`,
                  willChange: 'opacity',
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: isMobile
            ? 'linear-gradient(to bottom,rgba(47,43,40,0.45) 0%,rgba(47,43,40,0.60) 100%)'
            : 'linear-gradient(to bottom,rgba(47,43,40,0.35) 0%,rgba(47,43,40,0.50) 100%)',
        }}
      />

      {/* Dots */}
      <div className="absolute bottom-28 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {heroImages.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${i === current ? 'bg-brand-vintage w-6' : 'bg-brand-vintage/40 w-1.5'}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 px-6 max-w-5xl mx-auto flex flex-col items-center mt-12">
        <FadeIn delay={0.1}>
          <span className="text-brand-vintage/80 uppercase tracking-[0.4em] text-[10px] mb-8 block font-medium">
            Historic Southern California Estate
          </span>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-8xl text-brand-vintage mb-10 leading-[1.05] font-light">
            A Wedding Venue That Feels Like a{' '}
            <span className="italic text-brand-aged">Hidden Find</span>
            {' '}— But Looks Like a{' '}
            <span className="italic">Dream</span>
          </h1>
        </FadeIn>
        <FadeIn delay={0.3}>
          <p className="font-sans text-base md:text-lg text-brand-vintage/90 mb-12 max-w-2xl mx-auto leading-relaxed font-light tracking-wide">
            Historic charm, scenic foothill views, and elegant indoor-outdoor spaces thoughtfully designed for a setting your guests will remember long after the last dance.
          </p>
        </FadeIn>
        <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <a
            href="#contact"
            className="bg-brand-olive text-brand-vintage px-10 py-4 text-[10px] uppercase tracking-[0.25em] hover:brightness-110 transition-all shadow-2xl font-bold"
          >
            See If Your Date Feels Right
          </a>
          <a
            href="#weddings"
            className="border border-brand-vintage/50 text-brand-vintage px-10 py-4 text-[10px] uppercase tracking-[0.25em] hover:bg-brand-vintage hover:text-brand-charcoal transition-all backdrop-blur-sm"
          >
            Explore the Venue
          </a>
        </FadeIn>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-brand-vintage/40 flex flex-col items-center gap-3 animate-pulse">
        <span className="text-[8px] uppercase tracking-[0.3em]">Discover</span>
        <div className="w-[1px] h-16 bg-brand-vintage/30" />
      </div>
    </section>
  );
};

export default Hero;